import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { compareCatalogNames, type VodGenre, type VodKind } from '@player/domain';
import type { FocusPort } from '@player/ports';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';
import { scrollFocusedIntoView } from '../scrollFocusedIntoView.js';

/**
 * Tutti i generi (03-SCHERMATE-TV §3.6): «I tuoi generi» in evidenza, poi tutti dalla A alla Z.
 *
 * ## Perché serve, e non è una comodità
 *
 * La vetrina mostra **cinque** pastiglie e tre fasce di genere. Su un'installazione vera le categorie
 * sono ottanta, con nomi lunghi — `⭐️ GERMANY SKY CINEMA HD - FHD ⭐️` — e una fila orizzontale di
 * cinque nomi così non solo è scomoda: **nasconde le altre settantacinque**. Senza questa schermata,
 * tre quarti del catalogo non sono raggiungibili in nessun modo.
 *
 * ## In ordine alfabetico, con il conto accanto
 *
 * Le pastiglie della vetrina sono ordinate per ricchezza — «da dove comincio». Qui l'ordine è
 * l'alfabeto, perché la domanda è un'altra: «dov'è quella che sto cercando». Un elenco di ottanta
 * voci ordinato per numero di titoli si scorre due volte e non si trova niente.
 */

export interface VodGenresScreenProps {
  readonly focusPort: FocusPort;
  readonly kind: VodKind;
  /** I generi di chi guarda, in evidenza: sono le stesse pastiglie della vetrina. */
  readonly yours: ReadonlyArray<VodGenre>;
  /** Tutti, con il loro conteggio. Questa schermata li riordina per nome. */
  readonly all: ReadonlyArray<VodGenre>;
  readonly onChoose: (genreId: string) => void;
  readonly onExit: () => void;
  /** Quante colonne nella griglia. Cinque nel disegno. */
  readonly columns?: number;
}

const DEFAULT_COLUMNS = 5;

export function VodGenresScreen({
  focusPort,
  kind,
  yours,
  all,
  onChoose,
  onExit,
  columns = DEFAULT_COLUMNS,
}: VodGenresScreenProps): ReactNode {
  const translator = useTranslator();
  const [cursor, setCursor] = useState(0);

  const handlers = useRef({ onChoose, onExit });
  useEffect(() => {
    handlers.current = { onChoose, onExit };
  }, [onChoose, onExit]);

  /*
    Un elenco solo per muoversi: prima i tuoi, poi tutti in ordine alfabetico.

    Due griglie separate vorrebbero dire due cursori e una regola per passare dall'una all'altra —
    cioè un posto in più dove il fuoco può perdersi. Qui è una sequenza sola, e il titolo intermedio è
    solo un'etichetta che sta in mezzo.
  */
  const entries = useMemo(() => {
    // Per come si legge il nome, non per l'emoji con cui comincia: vedi `catalogSort.ts`.
    const alphabetical = [...all].sort((a, b) => compareCatalogNames(a.name, b.name));
    return [...yours, ...alphabetical];
  }, [yours, all]);

  const move = useCallback(
    (delta: number) => {
      setCursor((at) => {
        const next = at + delta;
        // Ai bordi si resta: su una griglia da ottanta voci, un fuoco che torna in cima dal fondo fa
        // perdere il posto senza che si capisca perché.
        if (next < 0 || next >= entries.length) return at;
        return next;
      });
    },
    [entries.length],
  );

  useEffect(() => {
    return focusPort.subscribe((key) => {
      if (key.kind === 'back') {
        handlers.current.onExit();
        return true;
      }
      if (key.kind === 'select') {
        const chosen = entries[cursor];
        if (chosen) handlers.current.onChoose(chosen.id);
        return true;
      }
      if (key.kind !== 'direction') return false;
      if (key.direction === 'left') move(-1);
      if (key.direction === 'right') move(1);
      if (key.direction === 'up') move(-columns);
      if (key.direction === 'down') move(columns);
      return true;
    });
  }, [focusPort, entries, cursor, columns, move]);

  const frameRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    scrollFocusedIntoView(frameRef.current?.querySelector<HTMLElement>('[data-focused="1"]'));
  }, [cursor]);

  /** Dove finiscono «i tuoi» e cominciano tutti: serve solo a sapere dove va il titolo di mezzo. */
  const yoursCount = yours.length;

  return (
    <div
      ref={frameRef}
      data-vod-genres={kind}
      style={{
        flex: 1,
        minWidth: 0,
        overflowY: 'auto',
        scrollbarWidth: 'none',
        padding: '56px 72px 80px',
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <h1 style={{ font: `800 64px ${FONT_STACK}`, letterSpacing: '-.02em', margin: 0 }}>
          {translator.t(kind === 'movie' ? 'vod.genres.title.movies' : 'vod.genres.title.series')}
        </h1>
        <span style={{ font: `500 24px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('vod.genres.subtitle', { count: all.length })}
        </span>
      </div>

      {yoursCount > 0 && (
        <>
          <span style={sectionLabelStyle}>{translator.t('vod.yourGenres')}</span>
          <Grid
            entries={yours}
            columns={columns}
            offset={0}
            cursor={cursor}
            onPick={(id) => handlers.current.onChoose(id)}
          />
        </>
      )}

      <span style={{ ...sectionLabelStyle, paddingTop: 20 }}>{translator.t('vod.genres.alphabetical')}</span>
      <Grid
        entries={entries.slice(yoursCount)}
        columns={columns}
        offset={yoursCount}
        cursor={cursor}
        onPick={(id) => handlers.current.onChoose(id)}
      />
    </div>
  );
}

function Grid({
  entries,
  columns,
  offset,
  cursor,
  onPick,
}: {
  entries: ReadonlyArray<VodGenre>;
  columns: number;
  /** Di quanto l'indice di questa griglia è spostato dentro la sequenza unica del cursore. */
  offset: number;
  cursor: number;
  onPick: (genreId: string) => void;
}): ReactNode {
  const translator = useTranslator();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))`, gap: 16 }}>
      {entries.map((genre, index) => {
        const focused = cursor === offset + index;
        return (
          <button
            key={`${offset}-${genre.id}`}
            type="button"
            data-f="1"
            data-genre={genre.id}
            data-focused={focused ? '1' : '0'}
            onClick={() => onPick(genre.id)}
            style={{
              minHeight: 100,
              padding: '12px 24px',
              border: 0,
              borderRadius: 14,
              background: colors.surface,
              color: colors.text,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              textAlign: 'left',
              minWidth: 0,
              cursor: 'pointer',
              outline: focused ? `4px solid ${colors.accent}` : 'none',
              outlineOffset: 4,
              transform: focused ? 'scale(var(--fs2, 1.02))' : 'none',
              transition: 'var(--tr, transform .16s ease)',
            }}
          >
            <span
              style={{
                /*
                  Due righe, poi i puntini.

                  I nomi veri arrivano a trentatré caratteri — `⭐️ GERMANY SKY CINEMA HD - FHD ⭐️` — e
                  su una riga sola si taglierebbero tutti allo stesso punto, cioè prima della parte
                  che li distingue.
                */
                font: `600 25px/1.2 ${FONT_STACK}`,
                minWidth: 0,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {genre.name}
            </span>
            {/*
              «3 sottocategorie», in ambra sotto il nome: dice **prima di premere** che lì dentro
              c'è un altro livello e non una griglia piatta. Senza, chi apre «Azione» e trova delle
              pastiglie non capisce da dove siano uscite.
            */}
            {genre.subcategoryCount !== undefined && genre.subcategoryCount > 0 && (
              <span
                data-genre-subcount="1"
                style={{ flex: 'none', font: `600 18px ${FONT_STACK}`, color: colors.accent }}
              >
                {translator.t('vod.genres.subcategoryCount', { count: genre.subcategoryCount })}
              </span>
            )}
            <span style={{ flex: 'none', font: `500 20px ${FONT_STACK}`, color: colors.textDim }}>
              {genre.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const sectionLabelStyle = {
  font: `600 20px ${FONT_STACK}`,
  color: colors.textDim,
  letterSpacing: '.06em',
  textTransform: 'uppercase' as const,
};
